import { Plugin } from '@tiptap/pm/state';
import { ReplaceAroundStep, ReplaceStep } from '@tiptap/pm/transform';

interface PreventNodeDeletionOptions {
  /** Array of node type names to protect from deletion */
  nodeNames: string[];
  /**
   * If true, allows edits within the protected node (updates that preserve the node type).
   * If false, blocks all modifications to the protected node.
   * @default false
   */
  allowEdits?: boolean;
}

/**
 * Creates a ProseMirror plugin that prevents deletion of specific node types.
 *
 * This plugin intercepts all document-modifying transactions and blocks any
 * that would delete nodes of the specified types. Useful for protecting
 * critical nodes (like widgets) from accidental deletion via backspace,
 * delete, cut, or select-all operations.
 *
 * @param options - Configuration options
 * @param options.nodeNames - Array of node type names to protect from deletion
 * @param options.allowEdits - If true, allows content edits within the node while still
 *   preventing deletion. Defaults to false (blocks all modifications).
 * @returns A ProseMirror Plugin instance
 *
 * @example
 * ```ts
 * // Prevent deletion and all edits (e.g., for a goals widget)
 * addProseMirrorPlugins() {
 *   return [createPreventNodeDeletionPlugin({ nodeNames: ['goalsNode'] })];
 * }
 *
 * // Prevent deletion but allow content edits
 * addProseMirrorPlugins() {
 *   return [createPreventNodeDeletionPlugin({
 *     nodeNames: ['headerNode'],
 *     allowEdits: true,
 *   })];
 * }
 * ```
 */
export const createPreventNodeDeletionPlugin = (
  options: PreventNodeDeletionOptions,
): Plugin => {
  const { nodeNames, allowEdits = false } = options;
  const protectedNodeNames = new Set(nodeNames);

  return new Plugin({
    filterTransaction: (transaction, state) => {
      // Allow all non-doc-changing transactions
      if (!transaction.docChanged) {
        return true;
      }

      // Check if this transaction would delete a protected node
      let shouldBlock = false;

      transaction.steps.forEach((step) => {
        if (step instanceof ReplaceStep || step instanceof ReplaceAroundStep) {
          const { from, to } = step;

          // Check if any protected node exists in the range being modified
          state.doc.nodesBetween(from, to, (node) => {
            if (protectedNodeNames.has(node.type.name)) {
              if (allowEdits) {
                // When allowEdits is true, only block if the node is being deleted
                // (i.e., not replaced with the same node type)
                const sliceContent = step.slice.content;
                const replacementHasSameNodeType =
                  sliceContent.childCount > 0 &&
                  sliceContent.firstChild?.type.name === node.type.name;

                if (!replacementHasSameNodeType) {
                  shouldBlock = true;
                  return false; // Stop iteration
                }
              } else {
                // Block all modifications to the protected node
                shouldBlock = true;
                return false; // Stop iteration
              }
            }
          });
        }
      });

      return !shouldBlock;
    },
  });
};
